/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {get} from '@ion/core/src/environment/environment';
import {
  Application,
  Role,
  User
} from '@ion/core/src/types/entities';
import {
  getCurrentUserFields
} from './DefaultFields';
import {
  currentUser,
  rawQuery
} from './GraphQL';
import {
  createLink,
  resolvePermissions,
  resolveUser
} from './ResourceResolver';
import {
  CurrentUser as APITierCurrentUser
} from './types';

/**
 * Provides an encapsulation of the API-Tier GraphQL Current User Query which can be
 * used in place of the CTC Current User GET Request when an application integrates
 * API-Tier GraphQL Services.
 *
 * The `currentUserQuery` retrieves the current user from API-Tier GraphQL Services
 * with a set of default fields commonly referenced by applications, and casts the
 * result to a CTC `User` type for easy backwards compatibility.
 *
 * ```typescript
 *  import {User} from '@ion/core/src/types/entities';
 *  import {
 *    currentUserQuery
 *  } from '@ion/services/src/graphql/QueryAdapter';
 *  ...
 *
 *  try {
 *    // retrieve current user from graphql services and cast to ctc user entity
 *    // optionally passing additional fields to be returned in addition to the
 *    // default fields provided via getCurrentUserFields
 *    const user: User = await currentUserQuery(...);
 *  } catch (err) {
 *    ...
 *  }
 *
 * ```
 *
 * @see {@link resolveUser}
 * @see {@link apiTierCurrentUser}
 * @see {@link getCurrentUserFields}
 * @see {@link CurrentUser}
 * @see {@link User}
 *
 * @param   fields A list of additional user fields to be retrieved in addition to the
 *          default fields. Note, the fields argument takes precedence over the default
 *          fields. As such, client's can override the properties of the default fields
 *          based on their particular needs.
 * @param   serviceURL an optional Service URL from which CTC entity Links will be
 *          created for. If not provided, defaults to `get('serviceURL')`.
 * @returns  fields
 */
export const currentUserQuery = async(
  fields?: Array<keyof APITierCurrentUser>, serviceURL = get('serviceURL')): Promise<User> => (
  resolveUser(
    await currentUser(getCurrentUserFields(fields)), serviceURL
  )
);

/**
 * Defines the `initializationQuery` results type which is returned from the
 * `initializationQuery` and generated by the `createInitializationQueryResult`
 * utility;
 */
export interface InitializationQueryResult {
  role: Role,
  application: Application
}

/**
 * Provides an encapsulation of an API-Tier GraphQL application initialization
 * Query which retrieves the authenticated User's Role, and the Application's
 * extendedAttributes.
 *
 * The values retrieved from the `initializationQuery` from API-Tier GraphQL Services
 * are provided with a default set of fields commonly referenced by applications, and
 * casts the result to CTC `Role` and `Application` types for backwards compatibility.
 *
 * ```typescript
 *  import {
 *    initializationQuery
 *  } from '@ion/services/src/graphql/QueryAdapter';
 *  ...
 *
 *  try {
 *    const {role, application} = await initializationQuery(...);
 *  } catch (err) {
 *    ...
 *  }
 *
 * ```
 *
 * @see {@link APITierRole}
 * @see {@link APITierApplication}
 * @see {@link Role}
 * @see {@link Application}
 */
export const initializationQuery = async(
  roleId: number, appKey = get('appKey'), serviceURL = get('serviceURL')
): Promise<InitializationQueryResult> => {
  const results = await rawQuery<{SearchRole: any, ApplicationQuery: any}>(`
    SearchRole(filter: {id: ${roleId}}, page: 1, perPage: 1) {
      count
      items {
        id
        name
        account {
          id
          name
        }
        applicationKey
        inheritable
        permissions
        extendedAttribute {
          applicationKey
          value
        }
      }
    }
    ApplicationQuery(applicationKey: "${appKey}") {
      id
      name
      extendedAttribute {
        applicationKey
        value
      }
    }
  `);
  return createInitializationQueryResult(results, serviceURL);
};

/**
 * @private
 */
const createInitializationQueryResult = (results, serviceURL): InitializationQueryResult => {
  const {SearchRole:{items}, ApplicationQuery: application} = results;
  const role = items?.[0];
  return {
    application,
    role : {
      ...role,
      application : createLink({
        id       : role.applicationKey,
        url      : serviceURL,
        resource : 'applications',
        rel      : 'application'
      }),
      _self : createLink({
        id       : role.id,
        url      : serviceURL,
        resource : 'roles',
        rel      : '_self'
      }),
      permissions : resolvePermissions(role?.permissions, serviceURL)
    }
  };
};
